import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { WizardStepper } from '../../components/layout/WizardStepper';
import { AppNav } from '../../components/layout/AppNav';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { FileUpload } from '../../components/ui/FileUpload';
import { useWizardStore } from '../../store/wizard.store';
import { offersService } from '../../services/offers.service';
import { ocrService } from '../../services/ocr.service';
import { useToast } from '../../components/ui/Toast';
import { Upload, Sparkles, Loader2 } from 'lucide-react';

type CustomerInfoFormData = {
  nameEn: string;
  nameAr: string;
  crNumber: string;
  crExpiry: string;
  vatNumber: string;
  contactEmail: string;
  contactMobile: string;
  landline: string;
  addressEn: string;
  addressAr: string;
};

const WIZARD_STEPS = [
  { label: 'Product', path: '/catalog' },
  { label: 'Location & Coverage', path: '/offers/wizard/location' },
  { label: 'Cart', path: '/offers/wizard/cart' },
  { label: 'Customer', path: '/offers/wizard/customer' },
  { label: 'Auth. Person', path: '/offers/wizard/authorized-person' },
  { label: 'Summary', path: '/offers/wizard/summary' },
];

const EN_CHARS = /^[A-Za-z0-9 .,'&()-]+$/;
const AR_CHARS = /^[؀-ۿݐ-ݿ0-9 .,'،()-]*$/;
const SAUDI_MOBILE = /^(\+9665|05)\d{8}$/;

// Which fields each document type can prefill
const OCR_PREFILL_FIELDS: Record<string, (keyof CustomerInfoFormData)[]> = {
  customerCR: ['nameEn', 'nameAr', 'crNumber', 'crExpiry'],
  customerVAT: ['nameEn', 'nameAr', 'vatNumber'],
  nationalAddress: ['addressEn', 'addressAr'],
};

export const CustomerInfoStep: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { offerId, setStep, documents, setDocument } = useWizardStore();

  const [ocrLoading, setOcrLoading] = useState<Record<string, boolean>>({});
  const [ocrPrefilled, setOcrPrefilled] = useState<Record<string, boolean>>({});

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { isSubmitting, isDirty, errors },
  } = useForm<CustomerInfoFormData>({
    defaultValues: {
      nameEn: '', nameAr: '', crNumber: '', crExpiry: '',
      vatNumber: '', contactEmail: '', contactMobile: '',
      landline: '', addressEn: '', addressAr: '',
    },
  });

  const prefillDone = useRef(false);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (!offerId || prefillDone.current) return;
    offersService.get(offerId).then((offer) => {
      if (isDirtyRef.current) return;
      prefillDone.current = true;
      if (!offer.customer) return;
      const c = offer.customer;
      const hasData = Object.values(c).some((v) => v !== null && v !== '' && v !== undefined);
      if (!hasData) return;
      reset({
        nameEn: c.nameEn ?? '', nameAr: c.nameAr ?? '',
        crNumber: c.crNumber ?? '',
        crExpiry: c.crExpiry ? String(c.crExpiry).slice(0, 10) : '',
        vatNumber: c.vatNumber ?? '', contactEmail: c.contactEmail ?? '',
        contactMobile: c.contactMobile ?? '', landline: c.landline ?? '',
        addressEn: c.addressEn ?? '', addressAr: c.addressAr ?? '',
      });
    }).catch(() => undefined);
  }, [offerId, reset]);

  if (!offerId) { navigate('/catalog'); return null; }

  const handleDocumentChange = async (
    docKey: 'customerCR' | 'customerVAT' | 'nationalAddress',
    file: File | null,
    ocrDocType: 'CR' | 'VAT' | 'NATIONAL_ADDRESS',
  ) => {
    setDocument(docKey, file);
    if (!file) { setOcrPrefilled((p) => ({ ...p, [docKey]: false })); return; }

    setOcrLoading((prev) => ({ ...prev, [docKey]: true }));
    try {
      const result = await ocrService.extractDocument(file, ocrDocType, offerId);
      const prefillFields = OCR_PREFILL_FIELDS[docKey] ?? [];
      let anyPrefilled = false;
      prefillFields.forEach((field) => {
        const val = (result as Record<string, string | undefined>)[field];
        if (val && val.trim()) {
          setValue(field as keyof CustomerInfoFormData, val.trim(), { shouldDirty: true });
          anyPrefilled = true;
        }
      });
      if (anyPrefilled) {
        setOcrPrefilled((p) => ({ ...p, [docKey]: true }));
        showToast('Fields prefilled from document — please review', 'success');
      }
    } catch {
      // OCR is best-effort — silently continue
    } finally {
      setOcrLoading((prev) => ({ ...prev, [docKey]: false }));
    }
  };

  const onSubmit = async (data: CustomerInfoFormData) => {
    try {
      await offersService.saveCustomerInfo(offerId, data);
      setStep(4);
      navigate('/offers/wizard/authorized-person');
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      showToast(Array.isArray(msg) ? msg.join(', ') : (msg ?? 'Failed to save customer info'), 'error');
    }
  };

  const handleSaveExit = async () => {
    try { await offersService.saveCustomerInfo(offerId, getValues()); } catch { /* best-effort */ }
    navigate('/my-offers');
  };

  const OcrBadge = ({ docKey }: { docKey: string }) => {
    if (ocrLoading[docKey]) return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--p)', fontWeight: 600 }}>
        <Loader2 size={11} style={{ animation: 'spin .8s linear infinite' }} /> Reading document…
      </span>
    );
    if (ocrPrefilled[docKey]) return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--succ)', fontWeight: 600 }}>
        <Sparkles size={11} /> Prefilled from document
      </span>
    );
    return null;
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Customer Information" />
      <WizardStepper steps={WIZARD_STEPS} currentStep={3} />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ maxWidth: '720px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* ── Required Documents FIRST ─────────────────────────────────── */}
          <div className="card">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Upload size={15} color="var(--p)" />
              <span className="section-title" style={{ marginBottom: 0 }}>Required Documents</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>
                Documents will be read automatically to prefill the form below
              </span>
            </div>
            <div className="card-body">
              <p style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '16px' }}>
                Upload PDF, JPG, or PNG — max 5 MB each. Fields will be prefilled from the document.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '16px' }}>
                <div>
                  <FileUpload
                    label="Commercial Registration (CR)"
                    hint="CR document — prefills CR number, expiry, company name"
                    value={documents.customerCR}
                    onChange={(f) => handleDocumentChange('customerCR', f, 'CR')}
                    required
                  />
                  <div style={{ marginTop: 4, minHeight: 16 }}><OcrBadge docKey="customerCR" /></div>
                </div>
                <div>
                  <FileUpload
                    label="VAT Certificate"
                    hint="ZATCA VAT certificate — prefills VAT number, company name"
                    value={documents.customerVAT}
                    onChange={(f) => handleDocumentChange('customerVAT', f, 'VAT')}
                    required
                  />
                  <div style={{ marginTop: 4, minHeight: 16 }}><OcrBadge docKey="customerVAT" /></div>
                </div>
                <div>
                  <FileUpload
                    label="National Address Document"
                    hint="National address certificate — prefills address fields"
                    value={documents.nationalAddress}
                    onChange={(f) => handleDocumentChange('nationalAddress', f, 'NATIONAL_ADDRESS')}
                    required
                  />
                  <div style={{ marginTop: 4, minHeight: 16 }}><OcrBadge docKey="nationalAddress" /></div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Customer details (review & edit prefilled data) ───────────── */}
          <div className="card">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="section-title" style={{ marginBottom: 0 }}>Customer Details</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>
                Review and edit all fields before proceeding
              </span>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormField label="Company Name (English)" lang="en" required error={errors.nameEn?.message}
                  {...register('nameEn', { required: 'Company name in English is required', pattern: { value: EN_CHARS, message: 'English characters only' } })} />
                <FormField label="اسم الشركة" lang="ar" error={errors.nameAr?.message}
                  {...register('nameAr', { pattern: { value: AR_CHARS, message: 'Arabic characters only' } })} />
                <FormField label="CR Number" placeholder="10-digit number" maxLength={10} inputMode="numeric" required error={errors.crNumber?.message}
                  {...register('crNumber', { required: 'CR number is required', pattern: { value: /^\d{10}$/, message: 'Must be exactly 10 digits' } })} />
                <FormField label="CR Expiry Date" type="date" required error={errors.crExpiry?.message}
                  {...register('crExpiry', { required: 'CR expiry date is required', validate: (v) => { const d = new Date(v); if (isNaN(d.getTime())) return 'Invalid date'; return d > new Date() || 'CR expiry must be a future date'; } })} />
                <FormField label="VAT Number" placeholder="15-digit, starts & ends with 3" maxLength={15} required error={errors.vatNumber?.message}
                  {...register('vatNumber', { required: 'VAT number is required', pattern: { value: /^3\d{13}3$/, message: 'Must be 15 digits, starting and ending with 3' } })} />
                <FormField label="Contact Mobile" placeholder="+9665XXXXXXXX or 05XXXXXXXX" maxLength={14} required error={errors.contactMobile?.message}
                  {...register('contactMobile', { required: 'Contact mobile is required', pattern: { value: SAUDI_MOBILE, message: 'Enter a valid Saudi mobile' } })} />
                <FormField label="Contact Email" type="email" required error={errors.contactEmail?.message}
                  {...register('contactEmail', { required: 'Contact email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' } })} />
                <FormField label="Landline" error={errors.landline?.message} {...register('landline')} />
                <FormField label="Address (English)" lang="en" error={errors.addressEn?.message} {...register('addressEn')} />
                <FormField label="العنوان" lang="ar" error={errors.addressAr?.message} {...register('addressAr')} />
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <Button variant="ghost" type="button" onClick={handleSaveExit}>Save & Exit</Button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="ghost" type="button" onClick={() => navigate('/offers/wizard/cart')}>Back</Button>
              <Button type="submit" loading={isSubmitting}>Next: Authorized Person</Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};
